import React from 'react';
import PropTypes from 'prop-types';
import AdaptiveTrait from '../../AdaptiveTrait';
import { slugify } from 'transliteration';
import Select from '../Form/Select';
import css from '@src/styles/components/tabs.scss';
import Tab from './Tab';

const config = {
    classes: {
        active: css['active'],
    },
};

export default class Tabs extends React.Component {

    constructor(props) {

        super(props);

        new AdaptiveTrait(this);

        this.nodes = { body: null, tabs: null, select: null };

        this.state = {
            activeTabId: this.getDefaultActiveTabId(),
        };
    }
    
    static get propTypes() {

        return {
            className: PropTypes.string,
            children: PropTypes.oneOfType([ PropTypes.object, PropTypes.array ]),
            onChange: PropTypes.func,
            changeTabByHash: PropTypes.bool,
        };
    }

    componentDidMount() {

        if (this.props.changeTabByHash) {

            this.hashChangeEvent = () => {

                const hash = location?.hash.replace(/^#/, '');
                if (hash) {
    
                    // check hash exists in tabs
    
                    if (this.isHashAllowed(hash)) {

                        this.changeTab(hash);
                        this.nodes.tabs.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }
            };

            window.addEventListener('hashchange', this.hashChangeEvent);
        }
    }

    componentWillUnmount() {
        
        if (this.props.changeTabByHash)
            window.removeEventListener('hashchange', this.hashChangeEvent);
    }
    
    componentDidUpdate() {

        const

            lastTab = this.nodes.body
                ?.querySelector(`[data-role="tab"].${config.classes.active}`),

            activeTab = this.nodes.body
                ?.querySelector(`[data-role="tab"][data-id="${this.state.activeTabId}"]`);

        if (lastTab) {

            if (lastTab != activeTab) {

                lastTab.classList.remove(config.classes.active);
                if (activeTab)
                    activeTab.classList.add(config.classes.active);
            }
        }
        else {

            if (activeTab)
                activeTab.classList.add(config.classes.active);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    renderMobile() {

        const

            navigationCollection = this
                .getNavigation()
                .map((item) => {

                    if (typeof(item.text) == 'object')
                        item.text = item.text?.props?.children;

                    return (
                        <option key={ item.key } value={ item.key } selected={ this.isActiveTab(item) }>
                            { item.text }
                        </option>
                    );
                }),

            navigation = (
                <Select ref={ (node) => { this.nodes.select = node; } } className={ css['tabs__select'] } onChange={ (e) => this.onNavigationChange(e) }>
                    { navigationCollection }
                </Select>
            );

        return this.renderComponent(navigation);
    }

    renderDesktop() {

        const navigationCollection = this
            .getNavigation()
            .map((item) => {

                const classes = [css['tabs__navigation']];

                if (this.isActiveTab(item))
                    classes.push(config.classes.active);

                return (
                    <div className={ classes.join(' ') } key={ item.key } data-id={ item.key } data-role='tab'
                        onClick={ (e) => this.onNavigationClick(e) }>
                        { item.text }
                    </div>
                );
            });

        return this.renderComponent(navigationCollection);
    }

    renderComponent(navigation) {

        if (!this.state.activeTabId)
            return null;

        const classes = [css['tabs']];

        if (this.props.className)
            classes.push(this.props.className);

        return (
            <div className={ classes.join(' ') }>
                { this.renderHead(navigation) }
                <div className={ css['tabs__body'] } ref={ (node) => { this.nodes.body = node; } }>
                    { this.renderContent() }
                </div>
            </div>
        );
    }

    renderHead(navigation) {

        if (!navigation || (navigation.props && navigation.props.children.length < 2) || navigation.length < 2)
            return null;

        return (
            <div className={ css['tabs__head'] } ref={ (node) => { this.nodes.tabs = node; } }>
                { navigation }
            </div>
        );
    }

    renderContent() {
    
        return this.getTabs();
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    changeTab(activeTabId) {

        if (this.state.activeTabId != activeTabId) {

            this.setState({ activeTabId }, () => {

                if (this.props.onChange != undefined) {
                
                    this.props.onChange(new CustomEvent('change', { detail: {
                        value: activeTabId,
                        target: this.nodes.body.querySelector(`[data-role="tab"][data-id="${activeTabId}"]`), 
                    } }));
                }
            });
        }
    }

    updateNavigation(navigation) {

        if (AdaptiveTrait.isMobile() && this.nodes.select) {

            const newSelectItems = [];
            Object
                .keys(navigation)
                .forEach((key) => {

                    if (this.state.navigation[key] != undefined) {

                        this.state.navigation[key].text = navigation[key];

                        newSelectItems[key] = {
                            value: this.state.navigation[key].key,
                            content: this.state.navigation[key].text,
                            text: this.state.navigation[key].text,
                        };
                    }
                    else {

                        throw new Error(`Unknown tab ${key}`);
                    }
                });

            this.setState({ navigation: this.state.navigation });
            this.nodes.select.setState({ items: newSelectItems });
        }
    }

    remove(id) {

        id = id.toLowerCase();

        this.tabs = this.tabs.filter((el) => el.key != id);
        this.state.navigation = this.state.navigation.filter((el) => el.key != id);

        if (this.state.activeTabId)
            this.state.activeTabId = this.state.navigation[0]?.key;

        this.setState({
            navigation: this.state.navigation,
            activeTabId: this.state.activeTabId,
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // EVENTS
    //------------------------------------------------------------------------------------------------------------------

    onNavigationClick(e) {

        let { target } = e;

        if (target.dataset.role != 'tab')
            target = target.closest('[data-role="tab"]');

        this.changeTab(target.dataset.id);
    }

    onNavigationChange(e) {

        this.changeTab(e.detail.value);
    }

    //------------------------------------------------------------------------------------------------------------------
    // GETTERS & SETTERS
    //------------------------------------------------------------------------------------------------------------------

    isActiveTab(tab) {

        return tab.key == this.state.activeTabId;
    }

    isHashAllowed(hash) {

        let isHashAllowed = false;
        this
            .getChildren()
            .forEach((child) => {
                
                if (Tabs.generateTabId(child) == hash)
                    isHashAllowed = true;
            });

        return isHashAllowed;
    }

    getDefaultActiveTabId() {

        if (this.props.changeTabByHash) {

            // is change allowed

            const hash = location?.hash.replace(/^#/, '');
            if (hash) {

                // check hash exists in tabs

                if (this.isHashAllowed(hash))
                    return hash;
            }
        }

        const children = this.getChildren();
        children.forEach((child) => {

            if (child.props.isActive)
                return Tabs.generateTabId(child);
        });

        if (children[0] != undefined)
            return Tabs.generateTabId(children[0]);

        return null;
    }

    getNavigation() {

        if (this.state.navigation == undefined) {

            this.state.navigation = this
                .getTabs()
                .map((tab) => ({
                    key: tab.key,
                    text: tab.props.title,
                }));
        }

        return this.state.navigation;
    }

    getChildren() {

        if (!this.children) {
            
            this.children = (this.props?.children != undefined && this.props.children.length > 0)
                ? React.Children.toArray(this.props.children)
                : [];
        }

        return this.children;
    }

    getTabs() {

        if (this.tabs == undefined) {

            this.tabs = this
                .getChildren()
                .map((tab) => {

                    Tabs.validateTab(tab);

                    const
                        tabId = Tabs.generateTabId(tab),
                        classes = [css['tabs__tab']];

                    if (tabId == this.state.activeTabId)
                        classes.push(config.classes.active);

                    return <Tab
                        key={ tabId }
                        className={ classes.join(' ') }
                        title={ tab.props.title }
                        data-id={ tabId }
                        data-role={ 'tab' }
                        children={ tab.props.children } />;
                });
        }

        return this.tabs;
    }

    static validateTab(tab) {

        if (tab?.props?.id == undefined && tab?.props?.title == undefined)
            throw new Error('Title props is required by tab');
    }

    static generateTabId(tab) {

        return slugify(tab.props.id ?? tab.props.title);
    }

    static hasActiveContainer(el) {

        return !!el.closest(`[data-role="tab"].${config.classes.active}`);
    }
}